import { useQuery } from "react-query";

import { existSomeMoralPolicyholder, existSomePhysicalPolicyholder } from "@/components/features/User/utils";
import { isNumeric } from "@/core/string";
import mobileIntegration from "@/integration";
import { policyholderApi } from "@/shared/apis";
import { Identity, PolicyHolder, UserState } from "@/shared/domain";

export type PolicyholderApiHook = {
    getPolicyholder: (policyholderId?: number | string | null | undefined) => PolicyHolder;
    getAllPolicyholders: () => PolicyHolder[];
    getPolicyholders: () => PolicyHolder[];
    isUserInPolicyholders: () => boolean;
    existPhysicalPolicyholders: boolean;
    existMoralPolicyholders: boolean;
};

export const usePolicyholderApi = (isProEnvironment: boolean, user: Identity, userState: any): PolicyholderApiHook => {
    const { data: allPolicyholders = [] } = useQuery("policyholders", () => policyholderApi.getPolicyholders(), {
        enabled: !!(user?.isAuthenticated && userState !== UserState.PENDING && userState !== UserState.UNKNOWN),
    });

    const getAllPolicyholders = () => allPolicyholders;

    const getPolicyholders = () => {
        let policyholders = allPolicyholders;
        if (isProEnvironment !== null && !mobileIntegration.isNativeApp()) {
            policyholders = (policyholders || []).filter(
                (policyholder) => policyholder.isPhysicalPerson !== isProEnvironment,
            );
        }
        return policyholders;
    };

    const isUserInPolicyholders = (): boolean => {
        const policyholders = getPolicyholders();
        return policyholders.some((policyholder) => policyholder.policyHolderId === Number(user.policyHolderId));
    };

    const getPolicyholder = (policyholderId?: number | string | null | undefined): PolicyHolder | null => {
        if (!isNumeric(policyholderId)) {
            return null;
        }
        const policyholders = getAllPolicyholders();
        return policyholders.find((policyholder) => policyholder.policyHolderId === +policyholderId);
    };

    const existPhysicalPolicyholders = existSomePhysicalPolicyholder(allPolicyholders);

    const existMoralPolicyholders = existSomeMoralPolicyholder(allPolicyholders);

    return {
        getPolicyholder,
        getPolicyholders,
        getAllPolicyholders,
        isUserInPolicyholders,
        existPhysicalPolicyholders,
        existMoralPolicyholders,
    };
};
